/**
 * The pure logic behind the three wizard screens that DO something: get the practice a number, hand
 * the setup to a person, and mint a sign-in link for someone who is not in the room.
 *
 * ── WHY THIS IS A .js MODULE AND NOT METHODS ON THE .vue FILES ───────────────────────────────────
 * Same argument `wizardSteps.js` makes. A plain ES module can be LOADED AND EXECUTED by the
 * node-tests (the phiMask.js precedent), so the sentence a test sweeps, the payload a test asserts
 * on and the phone number a test validates are the REAL ones — not a mirror written next to the
 * implementation and free to agree with itself forever. Logic that lives inside a `.vue` can only
 * be checked by grepping its source, which proves the code says something, never that it does it.
 *
 * ── THERE IS NO PRICE ON THIS SIDE ANY MORE (2026-09-02 — Saumil's decision) ─────────────────────
 * SOURCE: conversations-service/waOnboarding.js `myPurchaseNumber`, re-read 2026-09-02. Teamio
 * absorbs the cost of the WhatsApp number, so the practice is shown no price and the endpoint reads
 * no price: the request body is `{ siteID, idToken, phoneNumber }` and nothing more.
 *
 * DELETED IN THAT CHANGE, and deliberately not replaced: `CURRENCY_SYMBOLS`, `quoteAmount`,
 * `quoteCurrency`, `formatQuoteAmount`, `formatMonthlyPrice` and `acceptedPriceFrom`. They existed
 * to render a figure and echo it back for the server to match — a handshake ported from the A2P
 * wizard by mistake, against the standing rule that Twilio number prices are never customer-facing.
 * The server still price-checks the buy against its own quote and an internal ceiling; that is
 * server-side only and has no representation here. Leaving a price formatter in this file would be
 * an invitation to render one again, so there isn't one.
 */

/** The public page the connect link points at. SOURCE: src/Routes.js — the `/whatsapp-connect` route. */
export const CONNECT_PATH = '/whatsapp-connect';

/**
 * How long a customer-minted connect link lasts.
 *
 * SOURCE: conversations-service/waOnboarding.js `SELF_START_TOKEN_TTL_MS`, read 2026-09-02
 * (commit 7c27bc21) — `60 * 60 * 1000`, with the comment "minted per click, consumed same session
 * — 1h, NOT the operator link's 7 days".
 *
 * ⚠️ IT IS AN HOUR, NOT A WEEK. The operator's `/wa-onboarding/init` mints a 7-day link and returns
 * `expiresInDays: 7`; `my-connect-init` — the one a practice can call — returns `{ token }` alone
 * and the token dies in an hour. Telling a practice "this link works for a week" would have them
 * email it to their marketing agency, who would open it tomorrow to an expired page and blame us.
 * Mirrored here with a snapshot test so a backend change to that constant fails loudly in a test
 * instead of quietly in someone's inbox.
 */
export const CONNECT_LINK_TTL_MS = 60 * 60 * 1000;

/** How the link's lifetime is said to a practice. Words, not digits — and it must stay TRUE. */
export const CONNECT_LINK_TTL_LABEL = 'one hour';

/** The handoff reasons the backend accepts. SOURCE: waOnboarding.js `HANDOFF_REASONS`, 2026-09-02. */
export const HANDOFF_REASONS = ['byo', 'local_number', 'unknown_country', 'other'];

/**
 * A typed number reduced to the E.164 string we would send, or '' when it cannot be one.
 *
 * THE LEADING `+` IS REQUIRED, and that is the whole point of this function. Ten digits typed
 * without a country code look like a US number to a person and parse as a perfectly valid
 * `+20…` (Egypt) to a machine — and the next thing that happens is a real purchase of a real
 * number in the wrong country, charged monthly, which cannot be released once in use. Formatting
 * characters a human would type (spaces, dashes, brackets, dots) are stripped; a missing `+` is
 * refused rather than guessed at.
 */
export function normalizePhoneNumber(raw) {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (value.charAt(0) !== '+') return '';
  const digits = value.slice(1).replace(/[^0-9]/g, '');
  return digits === '' ? '' : `+${digits}`;
}

/**
 * Is this a number we are willing to send to the money endpoint?
 *
 * E.164: a `+`, a country code that cannot start with 0, and 8–15 digits in total. Stricter than
 * the backend (which forwards any non-blank string to the a2p core) in the SAFE direction — the
 * frontend may refuse what the server would accept, never the reverse.
 */
export function isValidPhoneNumber(raw) {
  return /^\+[1-9][0-9]{7,14}$/.test(normalizePhoneNumber(raw));
}

/**
 * The sentence in the confirm dialog, restating the one fact that makes this irreversible.
 *
 * REWRITTEN 2026-09-02. It used to restate a recurring charge and a price — both gone, because
 * Teamio pays for the number and the practice is shown nothing about what it costs. What survives is
 * the half that is still true and still matters: this number becomes the practice's WhatsApp
 * identity, and it cannot be swapped out once patients are messaging it. That is the reason there is
 * a dialog here at all, and it is enough of one.
 *
 * Takes ONLY the number now — a price argument would be a place for one to creep back in. Returns ''
 * for a blank number, so a caller can never open a dialog asking someone to approve a blank.
 */
export function purchaseConfirmMessage(phoneNumber) {
  const number = typeof phoneNumber === 'string' ? phoneNumber.trim() : '';
  if (number === '') return '';
  return `Set up ${number} as your WhatsApp number? Once it is in use it cannot be swapped out.`;
}

/**
 * The confirmation a handoff variant needs before it is fired, or '' when it needs none.
 *
 * ONLY the bring-your-own variant has one, and the asymmetry is the point. Handing a LOCAL number
 * to our team costs the practice nothing they can feel — they carry on as they are while we do the
 * paperwork. Handing over a number that is LIVE in the WhatsApp app on someone's phone means that
 * phone stops receiving WhatsApp during the move, which is a real interruption to a real practice
 * on a real day. The triage screen already warns about it; this is the point where they actually
 * set it in motion, so it is said once more with a yes/no in front of it.
 *
 * Returns '' for every other variant so the caller can treat "no message" as "no dialog" rather
 * than carrying a second list of which variants confirm.
 */
export function handoffConfirmMessage(variant) {
  if (variant !== 'byo') return '';
  return 'Hand this to our team? They will contact you to agree a time before anything changes. '
    + 'While your number is being moved, WhatsApp stops working on the phone it is on today.';
}

/**
 * The reason code for a handoff screen variant, or null for a variant this build does not know.
 *
 * Null rather than a default: `my-handoff` refuses an unregistered reason with `invalid_reason`,
 * and the reason is what our team reads in Slack to know WHY a practice is waiting on them. A
 * wrong-but-accepted default would page someone with a misleading story.
 */
export function handoffReason(variant) {
  if (variant === 'byo') return 'byo';
  if (variant === 'local') return 'local_number';
  return null;
}

/**
 * The full public URL for a minted connect token, or '' when there is no token.
 *
 * Same shape the operator panel builds (src/pages/Gautam/components/WhatsAppOnboarding.vue
 * `generateConnectLink`): the current origin plus the public route. The token is base64url, so the
 * encode is a no-op today and remains correct if that ever changes.
 */
export function connectLinkUrl(origin, token) {
  const value = typeof token === 'string' ? token.trim() : '';
  if (value === '') return '';
  const base = typeof origin === 'string' ? origin.replace(/\/+$/, '') : '';
  return `${base}${CONNECT_PATH}?token=${encodeURIComponent(value)}`;
}
